import { setItemToLocalStorage } from '@ordr-as/utils';
import type { FC } from 'react';
import { Suspense, lazy, useEffect, useRef } from 'react';
import type { Location } from 'react-router-dom';
import { Navigate, Route, Routes, useParams, useLocation as useRouterLocation } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { LocationContextProvider } from '../context/LocationContext';
import { SearchContextProvider } from '../context/SearchContext';
import {
  useQueryDigitalBillFeatureFlag,
  useQueryFailedPaymentPromptFeatureFlag,
  useQuickerLoginEnabled,
} from '../hooks/useFeatureFlagApi';
import { useQueryLocation, useQueryLocationInfo, useQueryLocationPaymentMethods } from '../hooks/useLocationApi';
import MenuSwitch from '../pages/Menu/MenuSwitch';
import { useUser } from '../context/AuthContext';
import { PaymentContextProvider } from '../context/PaymentContextProvider';
import { PrintReceiptContextProvider } from '../context/PrintReceiptContextProvider';
import DigitalBillSwitch from '../pages/DigitalBill/DigitalBillSwitch';
import StationBoardPage from '../pages/KdsBoard/StationBoardPage';
import ExpoBoardPage from '../pages/KdsBoard/ExpoBoardPage';
import { OrdrWebSocketProvider } from '../context/OrdrWebSocketProvider';
import Banner from '../components/Banner';
import { Userpilot } from 'userpilot';
import config from '../config';
import { CheckoutContextProvider } from '../context/CheckoutContext';
import { TableMapContextProvider } from '../context/table-map/TableMapContext';
import { DigitalBillDisplayModeContextProvider } from '../context/DigitalBillDisplayModeContext';
import { useCartStore } from '../context/cart/CartStore';
import { shallow } from 'zustand/shallow';
import { captureMessage } from '@sentry/react';
import { isAdyenDevice } from '../utils/isAdyenDevice';
import { useSoftpayAppId } from '../context/SoftpayAppIdStore';
import useDeviceId from '../hooks/useDeviceId';
import { HubProvider } from '../context/HubProvider';
import { usePaymentMethodActions } from '../context/PaymentMethodStore';
import { useFocusedPaymentMethodId } from '../hooks/useFocusedPaymentMethodId';
import { DeliveryMethodDtoType, useFindPlacements } from '@ordr-as/ordr-service-types/epos';
import { usePlacementsActions } from '../context/PlacementsStore';
import { useQueryActivePayments } from '../hooks/useQueryActivePayments';
import { useQueryMenuSections } from '../hooks/useQueryMenuSections';
import { useOpeningHoursActions } from '../context/OpeningHoursStore';
import { useMenuSectionsActions, useMenuSectionsLocationId } from '../context/MenuSectionsStore';
import { useDiscountActions, useDiscounts } from '../context/DiscountStore';
import { setTracingLocationId } from '../tracing';
import { useActiveRefunds } from '../hooks/useActiveRefunds';
import { useActivePaymentStore } from '../context/ActivePaymentStore';

const SettingsDialog = lazy(() => import('../pages/Settings/SettingsDialog'));
const CompletedPaymentsDialog = lazy(() => import('../pages/CompletedPayments/CompletedPaymentsDialog'));
const PinScreenSettingsDialog = lazy(() => import('../pages/PinScreenSettings/PinScreenSettingsDialog'));
const TerminalMode = lazy(() => import('../pages/TerminalMode/TerminalMode'));
const TableMapBuilderSwitch = lazy(() => import('../pages/TableMapBuilder/TableMapBuilderSwitch'));

const LocationSwitch: FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const routerLocation = useRouterLocation();
  const backgroundLocation = (routerLocation.state as { backgroundLocation?: Location } | null)?.backgroundLocation;
  const isDialogRoute = /\/(recent-payments|pin-settings|settings)(\/|$|\?)/.test(routerLocation.pathname);
  const showDialogRoutes = backgroundLocation || isDialogRoute;

  const { numericId, isError: locationListError } = useQueryLocation(locationId);

  const {
    data: location,
    isPending: locationPending,
    isError: locationError,
    error,
  } = useQueryLocationInfo(numericId);

  const {
    data: paymentMethodsData,
    isPending: paymentMethodsPending,
    isError: paymentMethodsError,
  } = useQueryLocationPaymentMethods(numericId);

  const isError = locationListError || locationError || paymentMethodsError;
  const isPending = numericId === undefined || locationPending || paymentMethodsPending;

  const { data: placements } = useFindPlacements(numericId ?? 0, {
    query: { enabled: numericId !== undefined },
  });

  // Prefetch using cached numericId / URL locationId (avoids waiting for location query)
  const menuQuery = useQueryMenuSections(numericId);
  const menuLocationId = useMenuSectionsLocationId();
  const { data: activePaymentsData } = useQueryActivePayments(locationId);
  useEffect(() => {
    if (activePaymentsData) {
      useActivePaymentStore.getState().actions.setPayments(activePaymentsData);
    }
  }, [activePaymentsData]);
  useQueryDigitalBillFeatureFlag(numericId);
  useQuickerLoginEnabled(numericId);
  useQueryFailedPaymentPromptFeatureFlag(locationId);
  const { setPlacements, clear: clearPlacements } = usePlacementsActions();
  const { clear: clearOpeningHours } = useOpeningHoursActions();
  const { clear: clearMenuSections } = useMenuSectionsActions();
  const { setDiscounts, clear: clearDiscounts } = useDiscountActions();
  const prevLocationId = useRef<number | undefined>();

  useEffect(() => {
    if (numericId !== undefined) {
      setTracingLocationId(numericId);
    }
  }, [numericId]);

  // Manage stores: clear on location change, set when data arrives
  useEffect(() => {
    // Clear when location changes (but not on initial mount)
    if (prevLocationId.current !== undefined && prevLocationId.current !== location?.id) {
      clearPlacements();
      clearOpeningHours();
      clearMenuSections();
      clearDiscounts();
    }
    prevLocationId.current = location?.id;

    // Set new placements when available
    if (placements) {
      setPlacements(placements);
    }

    // Set discounts when location is available
    if (location?.id) {
      setDiscounts(location.discounts, location.locationId);
    }
  }, [
    location?.id,
    placements,
    clearPlacements,
    setPlacements,
    clearOpeningHours,
    clearMenuSections,
    clearDiscounts,
    setDiscounts,
    location,
  ]);

  const softpayAppId = useSoftpayAppId();
  const deviceId = useDeviceId();
  const focusedPaymentMethodId = useFocusedPaymentMethodId();
  const { populateStore } = usePaymentMethodActions();

  useEffect(() => {
    if (location?.id && paymentMethodsData) {
      populateStore(paymentMethodsData.paymentMethods, focusedPaymentMethodId || softpayAppId || deviceId);
    }
  }, [deviceId, focusedPaymentMethodId, location?.id, paymentMethodsData, populateStore, softpayAppId]);

  const user = useUser();
  const userPilotUser = useRef<string | undefined>();

  const { cartLocationId, initCart } = useCartStore(
    (state) => ({
      cartLocationId: state.locationId,
      initCart: state.init,
    }),
    shallow
  );

  useEffect(() => {
    if (isAdyenDevice()) {
      // oxlint-disable-next-line no-console -- intentional startup diagnostic log for S1F2 devices
      console.log(
        `Instantiating ePOS on S1F2. Version ${config.release}. Terminal ${localStorage.getItem('device_id')}.`
      );
    }
  }, []);

  useEffect(() => {
    setItemToLocalStorage('favrit.epos.location', locationId);
  }, [locationId]);

  useEffect(() => {
    if (
      !isAdyenDevice() &&
      location?.locationId &&
      user &&
      (!userPilotUser.current || userPilotUser.current !== user.email)
    ) {
      userPilotUser.current = user.email;

      Userpilot.identify(user.email, {
        company: {
          id: location.locationGroupId.toString(),
          locationId: location.locationId,
          name: location.name,
        },
        email: user.email,
        role: user.role,
      });
    }
  }, [location, user]);

  const discounts = useDiscounts();
  useActiveRefunds();

  // Render loading/error states or location content
  const menuReady = numericId === undefined || menuLocationId === numericId || menuQuery.isError;
  const sectionsLoaded = menuLocationId === numericId;

  useEffect(() => {
    // Wait for menu sections to load before initializing cart, so discounts have computed productIds
    if (location?.locationId && cartLocationId !== location.locationId && sectionsLoaded) {
      const defaultVatType = location.deliveryMethods.some((dm) => dm.preSelected && dm.type === DeliveryMethodDtoType.TAKEAWAY)
        ? 'TAKEAWAY'
        : 'EAT_IN';
      initCart(location.locationId, discounts, localStorage, defaultVatType);
    }
  }, [cartLocationId, discounts, initCart, location, location?.locationId, sectionsLoaded]);

  const renderContent = () => {
    if (isPending) {
      return <Spinner className="m-auto mt-6 w-8 h-8" />;
    }

    if (isError && !location) {
      captureMessage(
        `Redirected to /locations in LocationSwitch because of location fetch error: ${String(error)}`,
        'debug'
      );
      return <Navigate to="/locations" replace />;
    }

    if (!location?.locationId) {
      captureMessage(`Failed to fetch locations in LocationSwitch somehow`, 'debug');
      return null;
    }

    if (!menuReady) {
      return <Spinner className="m-auto mt-6 w-8 h-8" />;
    }

    return (
      <LocationContextProvider location={location}>
        <PrintReceiptContextProvider>
          <Suspense fallback={<Spinner className="m-auto mt-6 w-8 h-8" />}>
            <Routes location={backgroundLocation || routerLocation}>
              <Route
                path="open-bill/*"
                element={
                  <TableMapContextProvider locationId={location.id}>
                    <DigitalBillDisplayModeContextProvider locationId={location.id}>
                      <SearchContextProvider>
                        <DigitalBillSwitch />
                      </SearchContextProvider>
                    </DigitalBillDisplayModeContextProvider>
                  </TableMapContextProvider>
                }
              />
              <Route
                path="kds/stations/:stationId/view"
                element={
                  <OrdrWebSocketProvider locationId={location.id}>
                    <StationBoardPage />
                  </OrdrWebSocketProvider>
                }
              />
              <Route
                path="kds/expo"
                element={
                  <OrdrWebSocketProvider locationId={location.id}>
                    <ExpoBoardPage />
                  </OrdrWebSocketProvider>
                }
              />
              <Route path="terminal-mode" element={<TerminalMode />} />
              <Route path="table-map-builder" element={<TableMapBuilderSwitch />} />
              <Route path="table-map-builder/:zoneId" element={<TableMapBuilderSwitch />} />
              <Route
                path="*"
                element={
                  <CheckoutContextProvider>
                    <PaymentContextProvider>
                      <SearchContextProvider>
                        <MenuSwitch />
                      </SearchContextProvider>
                    </PaymentContextProvider>
                  </CheckoutContextProvider>
                }
              />
            </Routes>
          </Suspense>
          {showDialogRoutes && (
            <Suspense fallback={null}>
              <Routes>
                <Route
                  path="recent-payments"
                  element={<CompletedPaymentsDialog locationId={location.id} locationIdStr={location.locationId} />}
                />
                <Route path="pin-settings" element={<PinScreenSettingsDialog />} />
                <Route path="settings" element={<SettingsDialog />} />
              </Routes>
            </Suspense>
          )}
        </PrintReceiptContextProvider>
      </LocationContextProvider>
    );
  };

  const content = renderContent();

  return (
    <>
      <Banner locationId={locationId!} />
      {numericId === undefined ? (
        content
      ) : (
        <HubProvider locationId={numericId} url={config.hubUrl}>
          {content}
        </HubProvider>
      )}
    </>
  );
};

export default LocationSwitch;
